import { inject } from '@angular/core';
import { CanActivateFn, Router, Routes, UrlTree } from '@angular/router';
import { ProposalsPage } from './proposals-page/proposals-page.component';
import { ActivePage } from './active-page/active-page.component';
import { InvitationsPage } from './invitations-page/invitations-page.component';
import { WorkspaceComponent } from './workspace/workspace.component';
import { CreateAccountComponent } from './create-account/create-account.component';
import { CreateAccountThanksComponent } from './create-account-thanks/create-account-thanks.component';
import { LoginComponent } from './login/login.component';
import { ForgotPasswordComponent } from './forgot-password/forgot-password.component';
import { ResetPasswordComponent } from './reset-password/reset-password.component';
import { SetPasswordComponent } from './set-password/set-password.component';
import { AuthGuard } from './helpers/auth.guard';
import { RoleGuard } from './helpers/role.guard';
import { setupLockGuard } from './helpers/setup-lock.guard';
import { getStoredUser } from './helpers/user-storage.util';
import { AuthenticationService } from './services/authentication.service';
import { ManageAccountComponent } from './manage-account/manage-account.component';
import { ContactUsComponent } from './contact-us/contact-us.component';
import { SettingsComponent } from './settings/settings.component';
import { ProjectHistoryPageComponent } from './project-history/project-history-page/project-history-page.component';
import { ProjectOverviewPageComponent } from './project-overview/project-overview-page/project-overview-page.component';
import { ManageGuard } from './helpers/manage.guard';
import { internalCanMatch } from './helpers/internal-match.guard';
import { DashboardComponent } from './dashboard/dashboard.component';
import { ReceivablesComponent } from './receivables/receivables.component';
import { SchedulingPageComponent } from './scheduling/scheduling-page.component';
import { ServicesZonesComponent } from './services-zones/services-zones.component';
import { DocumentsComponent } from './documents/documents.component';
import { PUBLIC_SITE_VISIT_RESPOND_ROUTE } from './site-visit/public-site-visit-route';
import { DEFAULT_RETURN_URL, resolveReturnUrl } from './helpers/return-url.util';

/**
 * Guards the root `''` path. An authenticated PA landing on `/` goes straight to
 * the Dashboard (Spec 3 decision); everyone else — unauthenticated, or any other
 * role — falls back to the same `/login` destination the unconditional
 * `redirectTo: 'login'` sent them to before this guard existed, so unauthenticated
 * and internal behavior at `''` is unchanged.
 *
 * Always resolves to a UrlTree (never `true`), so this route needs no component.
 */
const rootLandingGuard: CanActivateFn = (): UrlTree => {
  const user = getStoredUser();
  const userRole: string = user?.role || 'PROFESSIONAL_ASSOCIATE';
  const isAuthenticatedPa = !!user?.id && userRole === 'PROFESSIONAL_ASSOCIATE';

  const router = inject(Router);
  return isAuthenticatedPa
    ? router.createUrlTree(['/dashboard'])
    : router.createUrlTree(['/login']);
};

/**
 * Guards the `/login` path so an already-signed-in PA is not asked to sign in again — the point of
 * "keep me signed in" (a trusted device can be gone for up to 90 days and still return signed in, but
 * only if landing on the login page doesn't demand credentials anyway). `''` already did this via
 * {@link rootLandingGuard}; this closes the same gap for anyone arriving at `/login` directly — a
 * bookmark, a shared link, or simply where the URL was left after a previous logout.
 *
 * Two signals, deliberately BOTH required:
 *  - `isLoggedIn()` — the server-issued `connect_auth` hint cookie, which for a trusted session carries
 *    the same long life as the refresh cookie. This is real session state, not a client guess.
 *  - a stored PA profile — needed anyway to know the role, and keeps a cleared/edge-case storage from
 *    bouncing someone into a route their profile can't satisfy.
 *
 * The access token may well have expired (it is ~15 min); that is fine and expected — `AuthGuard` on
 * the destination calls `me()`, the auth interceptor silently refreshes on the 401, and a genuinely dead
 * session is hard-logged-out straight back here.
 *
 * INTERNAL staff deliberately stay on the login form, matching {@link rootLandingGuard}'s existing
 * treatment of non-PA users; changing internal routing is out of scope for this guard.
 *
 * **Task 7a:** an already-authenticated PA no longer bounces unconditionally to `/dashboard`. The PA
 * site-visit notification email links here with `?returnUrl=` set to the visit's project overview
 * page — without this, a "remember me" PA clicking that link would land on the dashboard instead of
 * the visit, exactly the friction that link exists to remove. `sanitizeReturnUrl` is what keeps this
 * from being an open redirect: the value arrived on a URL, not a trusted channel, so anything absolute
 * or scheme-bearing falls back to `/dashboard` rather than being followed.
 */
export const loginLandingGuard: CanActivateFn = (route): boolean | UrlTree => {
  const user = getStoredUser();
  const userRole: string = user?.role || 'PROFESSIONAL_ASSOCIATE';
  const isAuthenticatedPa =
    inject(AuthenticationService).isLoggedIn() &&
    !!user?.id &&
    userRole === 'PROFESSIONAL_ASSOCIATE';

  if (!isAuthenticatedPa) {
    return true;
  }
  const router = inject(Router);
  // parseUrl, not createUrlTree([...]): the value is a serialized URL that may carry its own query string
  // (the site-visit email now deep-links `/scheduling?visitId=...`). createUrlTree treats its argument as a
  // path segment and would percent-encode the `?` into the path, dropping the parameter without a trace.
  //
  // resolveReturnUrl, not sanitizeReturnUrl alone: a value can be perfectly legal — root-relative and
  // same-origin — and still not parse, and `Router.parseUrl` answers a rejected value with the ROOT tree
  // rather than throwing. Without that check a crafted `returnUrl` lands on `/`, not on the `/dashboard`
  // this comment and the sanitizer's contract both promise.
  const returnUrl = resolveReturnUrl(router, route.queryParamMap.get('returnUrl'));
  return router.parseUrl(returnUrl || DEFAULT_RETURN_URL);
};

export const routes: Routes = [
  { path: '', pathMatch: 'full', canActivate: [rootLandingGuard], children: [] },

  // --- Professional Associate routes (PA only) ---
  {
    path: 'dashboard',
    component: DashboardComponent,
    canActivate: [AuthGuard, RoleGuard, setupLockGuard],
    data: { requiredRole: 'PROFESSIONAL_ASSOCIATE' },
  },
  {
    path: 'receivables',
    component: ReceivablesComponent,
    canActivate: [AuthGuard, RoleGuard, setupLockGuard],
    data: { requiredRole: 'PROFESSIONAL_ASSOCIATE' },
  },
  // The sidebar's Services & Zones / Documents destinations — the standalone
  // pages lifted out of the legacy manage-account tabs (Spec 6, Tasks 1 & 2).
  {
    path: 'services-zones',
    component: ServicesZonesComponent,
    canActivate: [AuthGuard, RoleGuard, setupLockGuard],
    data: { requiredRole: 'PROFESSIONAL_ASSOCIATE' },
  },
  {
    path: 'documents',
    component: DocumentsComponent,
    canActivate: [AuthGuard, RoleGuard, setupLockGuard],
    data: { requiredRole: 'PROFESSIONAL_ASSOCIATE' },
  },
  {
    path: 'invitations',
    component: InvitationsPage,
    canActivate: [AuthGuard, RoleGuard, setupLockGuard],
    data: { requiredRole: 'PROFESSIONAL_ASSOCIATE' },
  },
  {
    path: 'proposals',
    component: ProposalsPage,
    canActivate: [AuthGuard, RoleGuard, setupLockGuard],
    data: { requiredRole: 'PROFESSIONAL_ASSOCIATE' },
  },
  // Pre-award per-site detail routes are retired in CONNECT 2.0 — the
  // Invitation/Proposal bid/quote/accept/decline actions now live inline on the
  // Spec 4 list cards. Redirect any lingering deep links back to their list.
  {
    path: 'proposals/:id',
    redirectTo: 'proposals',
    pathMatch: 'full',
  },
  {
    path: 'invitations/:id',
    redirectTo: 'invitations',
    pathMatch: 'full',
  },
  // Post-award per-site workspace (Spec 5A) — replaces ProposalDetails here.
  {
    path: 'active/:id',
    component: WorkspaceComponent,
    data: { type: 'active', requiredRole: 'PROFESSIONAL_ASSOCIATE' },
    canActivate: [AuthGuard, RoleGuard, setupLockGuard],
  },
  {
    path: 'active',
    component: ActivePage,
    canActivate: [AuthGuard, RoleGuard, setupLockGuard],
    data: { requiredRole: 'PROFESSIONAL_ASSOCIATE' },
  },
  {
    path: 'project-overview/:id',
    component: ProjectOverviewPageComponent,
    canActivate: [AuthGuard, RoleGuard, setupLockGuard],
    data: { requiredRole: 'PROFESSIONAL_ASSOCIATE' },
  },
  // Retired (Spec 9, Task 1): the per-site detail page this used to open
  // (ProposalDetails) is replaced by the Spec 4 nested cards inline on
  // /project-overview/:id itself — redirect any lingering deep link there.
  {
    path: 'project-overview-proposal/:id',
    redirectTo: 'project-overview/:id',
    pathMatch: 'full',
  },
  // The PA's cross-project scheduling queue (addendum Task 19). A top-level
  // authenticated route, deliberately: every site visit the vendor holds is on
  // one page, so a PA with visits on six projects has one place to look.
  //
  // Being a top-level route is also what makes "refetch on entering the scope"
  // free — the component is destroyed and recreated on each entry, so its
  // ngOnInit is the refetch and nothing needs to subscribe to the router.
  {
    path: 'scheduling',
    component: SchedulingPageComponent,
    canActivate: [AuthGuard, RoleGuard, setupLockGuard],
    data: { requiredRole: 'PROFESSIONAL_ASSOCIATE' },
  },
  {
    path: 'project-history',
    component: ProjectHistoryPageComponent,
    canActivate: [AuthGuard, RoleGuard, setupLockGuard],
    data: { requiredRole: 'PROFESSIONAL_ASSOCIATE' },
  },
  // Read-only per-site record (Spec 9, Task 4) — the completed-project variant
  // of the Spec 5 workspace, replacing the retired ProposalDetails usage. Same
  // guards + role as /active/:id; the `readOnly` flag drives the view-only shell.
  {
    path: 'project-history/:id',
    component: WorkspaceComponent,
    data: {
      type: 'project-history',
      readOnly: true,
      requiredRole: 'PROFESSIONAL_ASSOCIATE',
    },
    canActivate: [AuthGuard, RoleGuard, setupLockGuard],
  },

  // --- Internal routes ---
  {
    path: 'internal',
    loadComponent: () => import('./internal/internal.component').then((m) => m.InternalComponent),
    // canMatch runs before the lazy chunk is fetched — so non-INTERNAL users never
    // download the internal bundle at all. canActivate stays as the activation gate.
    canMatch: [internalCanMatch],
    canActivate: [AuthGuard, RoleGuard],
    data: { requiredRole: 'INTERNAL' },
    children: [
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
      {
        // Guard the child route explicitly — the parent canActivate does not re-run on
        // subsequent child navigations once the parent is already active (matches the audit child).
        path: 'dashboard',
        loadComponent: () =>
          import('./internal/internal-dashboard/internal-dashboard.component').then(
            (m) => m.InternalDashboardComponent,
          ),
        canActivate: [AuthGuard, RoleGuard],
        data: { requiredRole: 'INTERNAL' },
      },
      {
        // FIX 5: guard the child route explicitly — the parent canActivate does not
        // re-run on subsequent child navigations once the parent is already active.
        path: 'audit/:entityType/:entityId',
        loadComponent: () =>
          import('./internal/audit-timeline/audit-timeline.component').then(
            (m) => m.AuditTimelineComponent,
          ),
        canActivate: [AuthGuard, RoleGuard],
        data: { requiredRole: 'INTERNAL' },
      },
      {
        // Guarded like the sibling children above, plus ManageGuard on top so
        // only users with the canManage flag can reach the admin console.
        path: 'manage',
        loadComponent: () => import('./internal/manage/manage.component').then((m) => m.ManageComponent),
        canActivate: [AuthGuard, RoleGuard, ManageGuard],
        data: { requiredRole: 'INTERNAL' },
      },
      {
        // Catalogs (Payee · Order Type Catalog · Task Catalog) — same
        // MANAGE gating as the Management console.
        path: 'catalogs',
        loadComponent: () => import('./internal/catalogs/catalogs.component').then((m) => m.CatalogsComponent),
        canActivate: [AuthGuard, RoleGuard, ManageGuard],
        data: { requiredRole: 'INTERNAL' },
      },
      {
        // API (integration-client console) — moved out of the Management tab
        // group to its own top-level page. Same MANAGE gating as Management/
        // Catalogs; the component itself is unchanged (still lives under
        // manage/api, still ManageApiComponent).
        path: 'api',
        loadComponent: () => import('./internal/manage/api/manage-api.component').then((m) => m.ManageApiComponent),
        canActivate: [AuthGuard, RoleGuard, ManageGuard],
        data: { requiredRole: 'INTERNAL' },
      },
    ],
  },

  // --- Public routes ---
  //
  // 🔴 The site contact's no-login page. Deliberately the only route in this file with NO
  // `canActivate` and NO `canMatch`: the acceptance criterion is that it does not require a session,
  // and a page that happens to work because the opener was signed in is precisely the defect that
  // criterion is about. Authorization is possession of a live, audience-matched token, which
  // connect-service checks on every call; there is nothing here for a guard to add and a guard would
  // lock out the one audience the page exists for.
  //
  // Lazily loaded, so the site contact does not download the vendor portal to answer one question —
  // and so nothing in the authenticated bundle imports the component and gives a future refactor a
  // reason to link to it.
  {
    path: PUBLIC_SITE_VISIT_RESPOND_ROUTE,
    loadComponent: () =>
      import(
        './site-visit/public-site-visit-respond/public-site-visit-respond.component'
      ).then((m) => m.PublicSiteVisitRespondComponent),
  },
  { path: 'createaccount', component: CreateAccountComponent },
  { path: 'thanks', component: CreateAccountThanksComponent },
  { path: 'login', component: LoginComponent, canActivate: [loginLandingGuard] },
  { path: 'forgotpassword', component: ForgotPasswordComponent },
  // Forced reset after signing in with a temporary password. CONNECT 1.0 removed
  // it with CON-202, but on Connect 2.0 the admin console still issues temporary
  // passwords (create internal user, regenerate temp password, reset a vendor's
  // password) and login still sends a code-302 answer here. CON-158 retires it.
  { path: 'resetpassword', component: ResetPasswordComponent },
  // Opened from an emailed reset or invite link; main.ts has already taken the
  // token out of the fragment.
  { path: 'set-password', component: SetPasswordComponent },

  // --- Authenticated routes (any role) ---
  {
    path: 'manageaccount',
    component: ManageAccountComponent,
    canActivate: [AuthGuard, setupLockGuard],
  },
  {
    path: 'contact',
    component: ContactUsComponent,
    canActivate: [AuthGuard, setupLockGuard],
  },
  {
    path: 'settings',
    component: SettingsComponent,
    canActivate: [AuthGuard, setupLockGuard],
  },
];
